import rawTimeZones from "@vvo/tzdb/raw-time-zones.json";
import { fold, rankedFilter, type SearchTerms, termRank } from "../../common/option-search";
import { LOCAL_ZONE, TIME_ZONES } from "../../common/zone-clock";

export const KNOWN_ZONES = new Set(TIME_ZONES);

// A shared link carries anything at all, so a zone this browser cannot format is dropped rather than trusted.
export function pickZones(value: unknown): string[] {
  if (!Array.isArray(value)) return LOCAL_ZONE === "UTC" ? ["UTC"] : [LOCAL_ZONE, "UTC"];
  return [...new Set(value.filter((zone): zone is string => KNOWN_ZONES.has(zone as string)))];
}

// One zone rather than a row of them: what the calendar modes count in, since a month and a day are only a length
// once a zone has said which month and which day, and a shared link has to name it or it is the reader's own.
export function pickZone(value: unknown): string {
  return typeof value === "string" && KNOWN_ZONES.has(value) ? value : LOCAL_ZONE;
}

// Mantine's own filter is a substring match on the label, which here is the IANA name and nothing else: New Zealand
// answers with nothing, and Auckland is the only way to the zone Wellington keeps. So a zone is searched by every
// place it covers as well, ranked for the reason `countryFilter` is: "china" must not answer with Managua first.
export const zoneFilter = rankedFilter((zone, needle) => termRank(ZONE_TERMS.get(zone)?.search, needle), zoneFold);

// Why a zone answered, for the picker to put beside it: a row saying only Pacific/Auckland to somebody who searched
// for Wellington has hidden the one word that would explain itself. Nothing is offered for a search nobody has typed.
export function zoneMatches(zone: string, search: string): string[] {
  const needle = zoneFold(search.trim());
  if (!needle) return [];

  const kept: string[] = [];
  return (ZONE_TERMS.get(zone)?.places ?? []).flatMap((place) => {
    // One reason is enough where two say the same thing: New Zealand is the country, and New Zealand Time is the
    // clock named after it, and a row offering both has spent its width twice. The stronger band is the one kept.
    if (
      !place.folded.includes(needle) || kept.some((seen) => seen.includes(place.folded) || place.folded.includes(seen))
    ) {
      return [];
    }
    kept.push(place.folded);
    return [place.text];
  });
}

// An underscore is how the database spells a space, so both sides lose it and "new york" reaches America/New_York.
function zoneFold(value: string): string {
  return fold(value.replace(/_/g, " "));
}

// A zone is looked up by every name it has gone by, for the reason `local-country.ts` builds the same map: ICU's
// canonical spelling is whatever the zone was called when it was taken in, so browsers disagree about half of them.
// The names are written last so a zone's own entry beats the group it is also a member of — Antarctica/McMurdo is
// listed under Auckland's rules as well as under itself, and it is in Antarctica rather than in New Zealand.
const ZONE_FACTS = new Map<string, RawZone>([
  ...rawTimeZones.flatMap((zone) => zone.group.map((name) => [name, zone] as [string, RawZone])),
  ...rawTimeZones.map((zone) => [zone.name, zone] as [string, RawZone]),
]);

type RawZone = (typeof rawTimeZones)[number];

// What is searched and what is shown for it, built together so the picker cannot name a word the filter did not read.
interface ZoneTerms {
  search: SearchTerms;
  places: { text: string; folded: string }[];
}

const ZONE_TERMS = new Map(TIME_ZONES.map((zone) => [zone, zoneTerms(zone)]));

function zoneTerms(zone: string): ZoneTerms {
  const name = zoneFold(zone);
  const facts = ZONE_FACTS.get(zone);
  // UTC is the one zone in the list tzdb has no entry for, and the one that is nowhere and so has nothing else to it.
  if (!facts) {
    return {
      search: { name, codes: new Set([name]), whole: new Set([name]), aliases: new Set(), rest: [name] },
      places: [],
    };
  }

  // The cities are the group's rather than each member's — the seven Brazilian zones on Brasília time all list Rio —
  // so only the zone tzdb writes the group under is reached by them, and the other six by their country and by name.
  const cities = facts.name === zone ? facts.mainCities : [];

  // The offset alone off the front of the format, the rest of that string being the cities the band above holds.
  const [offset] = facts.rawFormat.split(" ");

  // The country, then the cities in it, then what the clock reads. Each band is a weaker claim on what was typed
  // than the one above: Chinandega is a city in Nicaragua and holds the letters of China, and McMurdo keeps New
  // Zealand time from Antarctica — so neither comes up before the country somebody actually named.
  const bands = [
    [facts.countryName, facts.continentName],
    cities,
    [facts.abbreviation, facts.alternativeName, offset],
  ];

  return {
    search: {
      name,
      codes: new Set([zoneFold(facts.abbreviation)]),
      // Australia is a part of Australia/Sydney's own name and only Macquarie's country, and Indian/… only begins
      // with India, where Kolkata is: a part typed in full is the label's, and a country or a city the next best.
      whole: new Set([name, ...name.split("/")]),
      aliases: new Set([facts.countryName, ...cities].map(zoneFold)),
      rest: bands.map((band) => zoneFold(band.join(" "))),
    },
    places: places(bands.flat(), name),
  };
}

// A word the label already says explains nothing beside it, so Auckland is dropped from Pacific/Auckland's reasons
// and Wellington is not; Antarctica is both a country and a continent, and is worth saying once.
function places(terms: string[], name: string): { text: string; folded: string }[] {
  const seen = new Set<string>();
  return terms.flatMap((text) => {
    const folded = zoneFold(text);
    if (!folded || seen.has(folded) || name.includes(folded)) return [];
    seen.add(folded);
    return [{ text, folded }];
  });
}
